import { Fragment, useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import {
  addToHomeData,
  clickedPromos,
  ContentfulResponse,
  generateBreadcrumbJoin,
  IContentfulResponse,
  useContentful,
  useMobile,
  useOnScreen,
  useSession,
  useSponsoredBannersHome,
  useUserWithMembership,
  visiblePromosScroll
} from '@smuchile/pkg-unimarc-hooks'
import {
  Container,
  Column,
  PromotionalModalProps,
  TimeOfferBannerProps,
  HtmlRenderProps,
  LinksListBox,
  MoreDetailBox,
  DownloadableTermsAndConditions,
  Text
} from '@smuchile/pkg-unimarc-components'
import {
  getBemId,
  getGlobalStyle
} from '@smuchile/pkg-unimarc-components/helpers'
import { IInAppModal } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/IInAppModal'
import {
  PropsHome,
  SendPromoDataImageBanner,
  SendPromoDataProps
} from 'shared/interfaces/Home'
import { RenderProductsCarouselProps } from 'components/ProductsCarousel/RenderProductsCarousel'
import {
  getPromotionalModalSeenCookie,
  setPromotionalModalSeenCookie
} from './PromotionalModal/helpers'
import { useAppDownloadModalContext } from 'components/AppDownloadModalWrapper/context'
import { BackboneShowCaseWeb } from './Backbone/BackboneShowCaseWeb'
import { BackboneHome } from './Backbone'
import { PropsBannersWeb } from './BannersWeb'
import { IShowCaseWebProps } from './ShowCaseWeb'
import { offerBannerValidator } from '../../shared/helpers/offerBannerValidator'
import { PagesContainerDescription } from 'components/PagesContainerDescription/PagesContainerDescription'
import { useFullHiderFooter } from 'shared/utils/hooks'
import {
  FeatureContentItem,
  FeatureContentItemFormat
} from 'shared/interfaces/ClubUnimarc'
import { Form } from 'components/Form'
import { BaseContainer } from '@smuchile/pkg-unimarc-components/stories/layout/BaseContainer/BaseContainer'
import { handleSponsoredBannersHome } from 'shared/helpers'
import { SponsoredBanner } from './SponsoredBanner'
import { ClubBlockFaq } from 'components/ClubPage/components/blocks/ClubBlockFaq'
import styles from './CoverageSwitch.module.css'

const COVERAGE_DELIVERY = 'Despacho a domicilio'
const COVERAGE_PICKUP = 'Retiro en tienda'
const COVERAGE_BANNER_LABEL = 'UNIMARC_WEB_Huincha'

const coverageOptions = {
  delivery: COVERAGE_DELIVERY,
  pickup: COVERAGE_PICKUP
} as const

type CoverageOption = keyof typeof coverageOptions

const getContentfulAssetUrl = (asset?: unknown): string => {
  if (!asset || typeof asset !== 'object') return ''

  return asset?.['fields']?.['file']?.['url'] || ''
}

interface CoverageSwitchProps {
  onChange: (value: CoverageOption) => void
  value: CoverageOption
}

const ShowCaseWeb = dynamic<IShowCaseWebProps>(
  () => {
    return import('./ShowCaseWeb').then((mod) => {
      return mod.ShowCaseWeb
    })
  },
  {
    loading: () => {
      return <BackboneShowCaseWeb />
    }
  }
)

const PromotionalModal = dynamic<PromotionalModalProps>(() => {
  return import('@smuchile/pkg-unimarc-components').then((mod) => {
    return mod.PromotionalModal
  })
})

const TimeOfferBanner = dynamic<TimeOfferBannerProps>(() => {
  return import('@smuchile/pkg-unimarc-components').then((mod) => {
    return mod.TimeOfferBanner
  })
})

const RenderStringHtml = dynamic<HtmlRenderProps>(() => {
  return import('@smuchile/pkg-unimarc-components').then((mod) => {
    return mod.RenderStringHtml
  })
})

const RenderProductsCarousel = dynamic<RenderProductsCarouselProps>(
  () => {
    return import('components/ProductsCarousel/RenderProductsCarousel').then(
      (mod) => {
        return mod.RenderProductsCarousel
      }
    )
  },
  { ssr: false }
)

const BannersWeb = dynamic<PropsBannersWeb>(() => {
  return import('./BannersWeb').then((mod) => {
    return mod.BannersWeb
  })
})

export const Home = ({
  contentTypeId = 'home',
  customMainPadding,
  data: serverData,
  hideMainFooter,
  isMobile,
  salesChannel
}: PropsHome) => {
  const router = useRouter()
  const { innerWidth } = useMobile()
  const [promotionalModalOpen, setPromotionalModalOpen] = useState(true)
  const [coverageOption, setCoverageOption] =
    useState<CoverageOption>('delivery')
  const { isAppDownloadModalBlockingLegacy } = useAppDownloadModalContext()

  const { isLoggedIn } = useSession()
  const containerPadding =
    customMainPadding || (isMobile ? '16px 0 0 0' : '32px 0 0 0')

  const { elementRef: refCase, isIntersecting: isIntersectingCase } =
    useOnScreen({ rootMargin: '0px' })
  const { elementRef: refTimer, isIntersecting: showTimer } = useOnScreen({
    rootMargin: '0px'
  })
  const { data: userMembership } = useUserWithMembership()
  const isFallback = router.isFallback
  const isMobileView = innerWidth > 0 ? isMobile || innerWidth < 1280 : isMobile
  const componentsToFirstLoad = isMobile ? 3 : 4
  const isCoverageLanding = router.asPath?.split('?')?.[0] === '/page/cobertura'

  let undefinedContent = 0
  let coverageSwitchRendered = false

  const customTitles = {
    h2: {
      customFontSize: isMobile
        ? getGlobalStyle('--font-size-titles-xs')
        : getGlobalStyle('--font-size-titles-lg')
    },
    h3: {
      customFontSize: isMobile
        ? getGlobalStyle('--font-size-titles-2xs')
        : getGlobalStyle('--font-size-titles-md')
    }
  }

  const clientData = useContentful({
    options: { content_type: contentTypeId },
    reactQuery: { enabled: !serverData }
  })
  const populatedData = clientData.data
    ? new ContentfulResponse(clientData.data as IContentfulResponse)
        .populateEntries()
        .getResponse()
    : []

  const promotionalModalResult = useContentful({
    options: {
      content_type: 'inAppModal',
      include: 1,
      limit: 1,
      'fields.idFormato': 1,
      'fields.startDate[lte]': new Date().toISOString(),
      'fields.endDate[gte]': new Date().toISOString(),
      'fields.platform': 'Web Unimarc eComm',
      'fields.status': true
    },
    reactQuery: { queryKey: 'promotionalModalWeb' }
  })

  const { data: sponsoredHomeBanners } = useSponsoredBannersHome({ isMobile })

  const promotionalModal: IInAppModal =
    promotionalModalResult?.data?.['items']?.[0]?.fields || null
  const promotionalModalAssets =
    promotionalModalResult.data?.['includes']?.Asset || []
  const promotionalModalImage = promotionalModalAssets?.[0]
  const shouldShowPromotionalModal = Boolean(
    promotionalModal && !isAppDownloadModalBlockingLegacy
  )
  const dataWithoutSponsor = serverData || populatedData

  const data = handleSponsoredBannersHome(
    dataWithoutSponsor,
    sponsoredHomeBanners
  )

  // custom hook to hide footer
  useFullHiderFooter(hideMainFooter)

  const handleTogglePromotionalModal = () => {
    setPromotionalModalOpen(!promotionalModalOpen)
  }

  const handleRenderValidate = ({ isRender = false, index = 0 }) => {
    return (
      isRender || index < componentsToFirstLoad || typeof window === 'undefined'
    )
  }

  const sendPromoData = ({ cfIndex, data, ref }: SendPromoDataProps) => {
    let promoData: SendPromoDataImageBanner = data
    promoData = {
      creativeName: promoData['creativeName'],
      creativeSlot: promoData['creativeSlot'],
      locationId: cfIndex,
      promotionId: promoData['promotionId'],
      promotionName: promoData['promotionName'],
      urlPath: promoData['reference']
    }
    addToHomeData({ ref, promoData })
  }

  const handleClickBanner = (promosData, index) => {
    clickedPromos({
      ...promosData,
      locationId: `${index + 1 - undefinedContent}`
    })
  }
  useEffect(() => {
    generateBreadcrumbJoin([])
    visiblePromosScroll({})
    const promotionalModalState = getPromotionalModalSeenCookie()
    if (!promotionalModalState) {
      setPromotionalModalOpen(true)
      setPromotionalModalSeenCookie(true)
    }
  }, [])

  if (!data || isFallback) {
    return <BackboneHome />
  }

  const dataEntries = Object.entries(data)
  const coverageBannerEntry = isCoverageLanding
    ? dataEntries.find(([key, value]) => {
        return (
          /featureContentBannersWeb/i.test(key) &&
          value?.['label'] === COVERAGE_BANNER_LABEL
        )
      })
    : null
  const coverageBannerIndex = dataEntries.findIndex(([key, value]) => {
    return (
      /featureContentBannersWeb/i.test(key) &&
      value?.['label'] === COVERAGE_BANNER_LABEL
    )
  })

  const renderCoverageBanner = () => {
    if (!coverageBannerEntry) return null

    return (
      <BannersWeb
        cfIndex={coverageBannerIndex + 1 - undefinedContent}
        data={coverageBannerEntry[1]}
        isMobile={isMobile}
        isPriority={false}
        saleChannel={salesChannel}
        titleSizes={customTitles}
      />
    )
  }
  return (
    <Container
      backgroundColor={getGlobalStyle('--color-base-white')}
      padding={containerPadding}
      position='relative'
      tagName='main'
    >
      {promotionalModalOpen && shouldShowPromotionalModal && (
        <PromotionalModal
          imageSrc={getContentfulAssetUrl(promotionalModalImage)}
          isMobile={isMobileView}
          linkWrapper={Link}
          onToggle={handleTogglePromotionalModal}
          open={promotionalModalOpen}
          redirectTo={promotionalModal?.actionUrl}
        />
      )}

      <Column
        alignItems='center'
        gap={isMobile ? 32 : 56}
      >
        {dataEntries.length > 0 &&
          dataEntries.map((content, index) => {
            // terms and conditions
            if (/term/i.test(content[0]) && content[1]?.subTitle) {
              return (
                <Column
                  alignItems='center'
                  key={index}
                  ref={refCase}
                >
                  <PagesContainerDescription
                    isMobile={isMobile}
                    items={content[1]}
                  />
                </Column>
              )
            }
            // carousel showcaseWeb
            if (
              /showcaseWeb/i.test(content[0]) &&
              content[1]?.['slider']?.length > 0
            ) {
              return (
                <Column
                  alignItems='center'
                  key={index}
                  ref={refCase}
                >
                  {handleRenderValidate({
                    isRender: isIntersectingCase,
                    index
                  }) && (
                    <ShowCaseWeb
                      cfIndex={index + 1 - undefinedContent}
                      data={content[1]['slider']}
                      isMobile={isMobile}
                      layout={content[1]['layout']}
                      marginBottom={content[1]['marginBottom']}
                      marginTop={content[1]['marginTop']}
                      mobileMarginBottom={content[1]['mobileMarginBottom']}
                      mobileMarginTop={content[1]['mobileMarginTop']}
                      salesChannel={salesChannel}
                    />
                  )}
                </Column>
              )
            }

            // time offer banner
            if (
              /offerBanner/i.test(content[0]) &&
              offerBannerValidator(content[1])
            ) {
              const itemSaleChannels =
                content[1]?.['image']?.['fields']?.['saleChannels']

              if (
                itemSaleChannels &&
                !itemSaleChannels?.includes(salesChannel)
              ) {
                return null
              }

              return (
                <Container
                  justifyContent='center'
                  key={index}
                  ref={refTimer}
                >
                  <Column alignItems='center'>
                    {handleRenderValidate({ isRender: showTimer, index }) && (
                      <TimeOfferBanner
                        bannerSalesChannel={
                          content[1]?.['image']?.['fields']?.['saleChannels']
                        }
                        catchPromoData={(promoData?: object) => {
                          sendPromoData({
                            cfIndex: `${index + 1 - undefinedContent}`,
                            data: content[1]?.['image']?.['fields'],
                            ref: promoData
                          })
                        }}
                        endDate={new Date(content[1]?.['endDate'])}
                        href={content[1]?.['image']?.['reference']}
                        imgSrcDesktop={
                          content[1]?.['image']?.['imageDesktop']?.['fields']?.[
                            'file'
                          ]?.['url']
                        }
                        imgSrcMobile={
                          content[1]?.['image']?.['imageMobile']?.['fields']?.[
                            'file'
                          ]?.['url']
                        }
                        isMobile={isMobile}
                        linkWrapper={Link}
                        onRedirect={() => {
                          handleClickBanner(
                            content[1]?.['image']?.['fields'],
                            index
                          )
                        }}
                        orderformSalesChannel={salesChannel}
                        startDate={new Date(content[1]?.['startDate'])}
                      />
                    )}
                  </Column>
                </Container>
              )
            }

            // banners
            if (/featureContentBannersWeb/i.test(content[0])) {
              if (
                isCoverageLanding &&
                content[1]?.['label'] === COVERAGE_BANNER_LABEL
              ) {
                return null
              }

              return (
                <BannersWeb
                  cfIndex={index + 1 - undefinedContent}
                  data={content[1]}
                  isMobile={isMobile}
                  isPriority={index === 0}
                  key={index}
                  saleChannel={salesChannel}
                  titleSizes={customTitles}
                />
              )
            }

            if (/productsCarouselWeb/i.test(content[0])) {
              const refType: string = content[1]?.['referenceType']

              if (!['MYUSUALS', 'COLLECTION'].includes(refType?.toUpperCase()))
                return null
              if (refType?.toUpperCase() === 'MYUSUALS' && !isLoggedIn)
                return null

              return (
                <RenderProductsCarousel
                  content={content[1]}
                  customTitles={customTitles}
                  index={index}
                  isMobile={isMobileView}
                  key={index}
                />
              )
            }

            if (/featureContentItem/i.test(content[0])) {
              const featureContentItem: FeatureContentItem = content[1]
              if (
                featureContentItem.itemFormat ===
                  FeatureContentItemFormat.CustomizedOffers &&
                isLoggedIn
              ) {
                const carouselContent = {
                  title: featureContentItem.title,
                  subtitle: featureContentItem.subtitle,
                  sectionIcon:
                    featureContentItem?.sectionIcon?.fields?.file?.url,
                  backgroundImage: featureContentItem?.items.find(
                    (imageBanner) => {
                      return imageBanner.unimarcEnabledLevels?.some(
                        (unimarcEnabledLevel) => {
                          return userMembership?.level?.includes(
                            unimarcEnabledLevel
                          )
                        }
                      )
                    }
                  )?.fields?.image?.fields?.file?.url,
                  referenceType: 'MYUSUALS',
                  url: ''
                }
                return (
                  <>
                    <RenderProductsCarousel
                      content={carouselContent}
                      customTitles={customTitles}
                      index={index}
                      isMobile={isMobile}
                      key={index}
                      type='myOffers'
                    />
                  </>
                )
              } else if (
                featureContentItem.itemFormat ===
                FeatureContentItemFormat.InteractiveTermsAndConditions
              ) {
                return (
                  <>
                    <BaseContainer
                      flexDirection='column'
                      maxWidth={getGlobalStyle('--width-max-desktop')}
                      padding={isMobile && '0 16px'}
                    >
                      {featureContentItem?.items?.map((document, index) => {
                        return (
                          <BaseContainer
                            key={document?.sys?.id}
                            margin={
                              featureContentItem?.items.length - 1 !== index &&
                              '0px 0px 32px'
                            }
                          >
                            <DownloadableTermsAndConditions
                              isMobile={isMobile}
                              title={document?.title}
                              url={document?.file?.fields?.file?.url}
                            />
                          </BaseContainer>
                        )
                      })}
                    </BaseContainer>
                  </>
                )
              } else if (
                featureContentItem.itemFormat ===
                FeatureContentItemFormat.FrequentlyAskedQuestion
              ) {
                const isCoverageSection =
                  isCoverageLanding &&
                  [COVERAGE_DELIVERY, COVERAGE_PICKUP].includes(
                    featureContentItem.title
                  )
                const shouldRenderCoverageSwitch =
                  isCoverageSection && !coverageSwitchRendered

                if (isCoverageSection) {
                  coverageSwitchRendered = true
                }

                if (
                  isCoverageSection &&
                  featureContentItem.title !== coverageOptions[coverageOption]
                ) {
                  return shouldRenderCoverageSwitch ? (
                    <Fragment key={index + featureContentItem.itemFormat}>
                      <CoverageSwitch
                        onChange={setCoverageOption}
                        value={coverageOption}
                      />
                      {renderCoverageBanner()}
                    </Fragment>
                  ) : null
                }

                return (
                  <Fragment key={index + featureContentItem.itemFormat}>
                    {shouldRenderCoverageSwitch && (
                      <>
                        <CoverageSwitch
                          onChange={setCoverageOption}
                          value={coverageOption}
                        />
                        {renderCoverageBanner()}
                      </>
                    )}
                    <BaseContainer
                      flexDirection='column'
                      maxWidth={getGlobalStyle('--width-max-desktop')}
                      padding={isMobile && '0 16px'}
                    >
                      {featureContentItem?.title && (
                        <BaseContainer
                          justifyContent='start'
                          padding='0px 0px 16px 0px'
                        >
                          <Text
                            fontSize={isMobile ? 'lg' : '3xl'}
                            fontWeight='regular'
                            id={getBemId('frequentlyAskedQuestion', 'title')}
                          >
                            {featureContentItem?.title}
                          </Text>
                        </BaseContainer>
                      )}
                      <ClubBlockFaq
                        description={featureContentItem?.description}
                        hideContentBorder={isCoverageLanding}
                        items={featureContentItem?.items}
                      />
                    </BaseContainer>
                  </Fragment>
                )
              }
              return null
            }

            if (/term/i.test(content[0]) && content[1]?.html) {
              return (
                <RenderStringHtml
                  htmlString={content[1].html}
                  key={index}
                />
              )
            }

            if (
              /term/i.test(content[0]) &&
              content[1]?.type === 'seo details'
            ) {
              return (
                <Container
                  key={index}
                  padding={isMobile ? '0 16px' : '0 32px'}
                >
                  <MoreDetailBox
                    backgroundColor={content[1]?.backgroundColor}
                    fontColor={content[1]?.fontColor}
                    isMobile={isMobile}
                    linksColor={content[1]?.linksColor}
                    richTextData={[content[1].text.content]}
                    title={content[1]?.title}
                  />
                </Container>
              )
            }

            if (/term/i.test(content[0]) && content[1]?.type === 'seo links') {
              return (
                <Container
                  key={index}
                  padding={isMobile ? '0 16px' : '0 32px'}
                >
                  <LinksListBox
                    backgroundColor={content[1]?.backgroundColor}
                    fontColor={content[1]?.fontColor}
                    isMobile={isMobile}
                    linksColor={content[1]?.linksColor}
                    richTextData={[content[1].text.content]}
                    title={content[1]?.title}
                  />
                </Container>
              )
            }

            if (/Form/i.test(content[0])) {
              const formType = content[1]?.type

              if (
                (isMobile && formType === 'App') ||
                (!isMobile && formType === 'Web')
              ) {
                return (
                  <Form
                    form={content[1]}
                    isMobile={isMobile}
                    key={index}
                  />
                )
              }
            }
            if (
              /sponsoredBanner/i.test(content[0]) &&
              router.pathname === '/'
            ) {
              return (
                <Container
                  justifyContent='center'
                  key={index}
                  padding={isMobile ? '0 16px' : '0 32px'}
                >
                  <SponsoredBanner
                    content={content?.[1]}
                    isMobile={isMobile}
                  />
                </Container>
              )
            }

            undefinedContent += 1
            return null
          })}
      </Column>
    </Container>
  )
}

const CoverageSwitch = ({ onChange, value }: CoverageSwitchProps) => {
  return (
    <div
      aria-label='Tipo de cobertura'
      className={styles['coverage-switch']}
      role='group'
    >
      {(Object.entries(coverageOptions) as Array<[CoverageOption, string]>).map(
        ([option, label]) => {
          const active = option === value

          return (
            <button
              aria-pressed={active}
              className={`${styles['coverage-switch__button']} ${
                active ? styles['coverage-switch__button--active'] : ''
              }`}
              key={option}
              onClick={() => {
                onChange(option)
              }}
              type='button'
            >
              {label}
            </button>
          )
        }
      )}
    </div>
  )
}
